/**
 * sqlite3mt slot protocol — §96 §10.1–§10.5
 *
 * The slot protocol is the TLA+-verified data-plane read/write path for
 * MainDb pages.  Client Workers call pinOrInstallSlotForReadLinearized()
 * to obtain a pinned slot (with SLOT_LOCK held) for a given page, then
 * inspect state, dispatch faults if needed, and eventually release the
 * slot lock.  Control Worker calls publishFaultState() to complete a
 * fault and xWriteMainDb() is a best-effort cache write.
 *
 * Lock ordering: PAGE_TABLE_LOCK → SLOT_LOCK[i].  No deadlock possible.
 */

import {
    DEFAULT_PAGE_SIZE,
    NUM_SLOTS,
    SAB1_DB_PAGE_COUNT_I32,
    SAB1_PAGE_TABLE_LOCK_I32,
    SlotMetaI32,
    sab1CtrlView,
    sab1LoadSlotGen,
    sab1SlotDataView,
    sab1SlotMetaView,
    sab1StoreSlotGen,
} from './sab-layout'
import { SlotState, type Pgno } from './types'

// ── Result types for pinOrInstallSlotForReadLinearized ──────────

/** Page is beyond logical EOF — caller should return SQLITE_IOERR_SHORT_READ. */
export interface PinResultEof {
    kind: 'EOF'
}

/**
 * Slot is pinned for pgno; SLOT_LOCK[slot] is held.
 * Caller MUST release it with unlockSlot() after reading or dispatching fault.
 */
export interface PinResultSlot {
    kind: 'SLOT'
    slot: number
    gen: bigint
    firstOwner: boolean
}

/**
 * No victim slot available (all are FETCHING).
 * Caller should yield and retry the entire pin operation.
 */
export interface PinResultRetry {
    kind: 'RETRY'
}

export type PinResult = PinResultEof | PinResultSlot | PinResultRetry

// ── Internal helpers ────────────────────────────────────────────

function acquireInt32SpinLock(view: Int32Array, index: number): void {
    for (;;) {
        const observed = Atomics.compareExchange(view, index, 0, 1)
        if (observed === 0) {
            return
        }
        Atomics.wait(view, index, observed)
    }
}

function releaseInt32SpinLock(view: Int32Array, index: number): void {
    Atomics.store(view, index, 0)
    Atomics.notify(view, index, 1)
}

export function acquirePageTableLock(sab1: SharedArrayBuffer): void {
    acquireInt32SpinLock(sab1CtrlView(sab1), SAB1_PAGE_TABLE_LOCK_I32)
}

export function releasePageTableLock(sab1: SharedArrayBuffer): void {
    releaseInt32SpinLock(sab1CtrlView(sab1), SAB1_PAGE_TABLE_LOCK_I32)
}

export function acquireSlotLock(
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    acquireInt32SpinLock(sab1SlotMetaView(sab1, slot, pageSize), SlotMetaI32.LOCK)
}

function releaseSlotLock(
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    releaseInt32SpinLock(sab1SlotMetaView(sab1, slot, pageSize), SlotMetaI32.LOCK)
}

/**
 * Find a slot that currently maps pgno.
 * Must be called with PAGE_TABLE_LOCK held.
 * Returns -1 if no matching slot found.
 */
function findSlotLocked(
    sab1: SharedArrayBuffer,
    pgno: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): number {
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        const meta = sab1SlotMetaView(sab1, slot, pageSize)
        const st = Atomics.load(meta, SlotMetaI32.STATE)
        if (st !== SlotState.EMPTY && Atomics.load(meta, SlotMetaI32.PGNO) === pgno) {
            return slot
        }
    }
    return -1
}

/**
 * Choose a victim slot for eviction.
 * Must be called with PAGE_TABLE_LOCK held.
 * Prefers EMPTY, then ERROR, then READY.
 * Returns -1 if no suitable victim (all are FETCHING or RETRY).
 */
function chooseVictimLocked(
    sab1: SharedArrayBuffer,
    pageSize: number = DEFAULT_PAGE_SIZE
): number {
    // Prefer EMPTY — no data to lose
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        const meta = sab1SlotMetaView(sab1, slot, pageSize)
        if (Atomics.load(meta, SlotMetaI32.STATE) === SlotState.EMPTY) return slot
    }
    // Then ERROR — already unusable
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        const meta = sab1SlotMetaView(sab1, slot, pageSize)
        if (Atomics.load(meta, SlotMetaI32.STATE) === SlotState.ERROR) return slot
    }
    // Then READY — evictable cache entry
    for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
        const meta = sab1SlotMetaView(sab1, slot, pageSize)
        if (Atomics.load(meta, SlotMetaI32.STATE) === SlotState.READY) return slot
    }
    // All slots are FETCHING or RETRY — no victim available
    return -1
}

// ── §10.2 pinOrInstallSlotForReadLinearized ─────────────────────

/**
 * Pin or install a slot for reading page pgno.
 *
 * §10.2 contract:
 * - Returns EOF if pgno > DB_PAGE_COUNT (linearised under PAGE_TABLE_LOCK)
 * - Returns SLOT with SLOT_LOCK held if a slot was obtained
 * - Returns RETRY if no victim slot is available; caller should yield + retry
 * - EOF decision is never lock-free
 * - On SLOT return, caller owns SLOT_LOCK and must release with unlockSlot()
 *
 * The function retries internally when:
 * - A found slot's pgno changes between find and lock acquisition
 * - A victim slot's state becomes FETCHING/RETRY between choose and lock
 */
export function pinOrInstallSlotForReadLinearized(
    pgno: number,
    sab1: SharedArrayBuffer,
    pageSize: number = DEFAULT_PAGE_SIZE
): PinResult {
    for (;;) {
        // Acquire PAGE_TABLE_LOCK
        acquirePageTableLock(sab1)

        // EOF check under PAGE_TABLE_LOCK — no lock-free EOF path
        const eof = Atomics.load(sab1CtrlView(sab1), SAB1_DB_PAGE_COUNT_I32)
        if (pgno > eof) {
            releasePageTableLock(sab1)
            return { kind: 'EOF' }
        }

        // Look for an existing slot for this pgno
        const existingSlot = findSlotLocked(sab1, pgno, pageSize)
        if (existingSlot >= 0) {
            const meta = sab1SlotMetaView(sab1, existingSlot, pageSize)
            acquireSlotLock(sab1, existingSlot, pageSize)

            // Re-check pgno under slot lock
            if (Atomics.load(meta, SlotMetaI32.PGNO) !== pgno) {
                releaseSlotLock(sab1, existingSlot, pageSize)
                releasePageTableLock(sab1)
                continue // retry — slot was re-purposed
            }

            const st = Atomics.load(meta, SlotMetaI32.STATE)
            if (st === SlotState.ERROR) {
                // Re-use ERROR slot: bump gen, set FETCHING, firstOwner
                const newGen = sab1LoadSlotGen(sab1, existingSlot, pageSize) + 1n
                sab1StoreSlotGen(sab1, existingSlot, newGen, pageSize)
                Atomics.store(meta, SlotMetaI32.STATE, SlotState.FETCHING)
                releasePageTableLock(sab1)
                return { kind: 'SLOT', slot: existingSlot, gen: newGen, firstOwner: true }
            }

            // Slot exists and is not ERROR — pin it (READY, FETCHING, RETRY)
            const gen = sab1LoadSlotGen(sab1, existingSlot, pageSize)
            releasePageTableLock(sab1)
            return { kind: 'SLOT', slot: existingSlot, gen, firstOwner: false }
        }

        // No existing slot — find a victim
        const victimSlot = chooseVictimLocked(sab1, pageSize)
        if (victimSlot < 0) {
            // All slots are FETCHING/RETRY — caller must yield and retry
            releasePageTableLock(sab1)
            return { kind: 'RETRY' }
        }

        const meta = sab1SlotMetaView(sab1, victimSlot, pageSize)
        acquireSlotLock(sab1, victimSlot, pageSize)

        // Verify the victim is still usable
        const st0 = Atomics.load(meta, SlotMetaI32.STATE)
        if (st0 !== SlotState.EMPTY && st0 !== SlotState.READY && st0 !== SlotState.ERROR) {
            // Victim became FETCHING/RETRY — try again
            releaseSlotLock(sab1, victimSlot, pageSize)
            releasePageTableLock(sab1)
            continue
        }

        // Install pgno into victim, bump gen, set FETCHING
        const newGen = sab1LoadSlotGen(sab1, victimSlot, pageSize) + 1n
        sab1StoreSlotGen(sab1, victimSlot, newGen, pageSize)
        Atomics.store(meta, SlotMetaI32.PGNO, pgno)
        Atomics.store(meta, SlotMetaI32.STATE, SlotState.FETCHING)
        releasePageTableLock(sab1)

        return { kind: 'SLOT', slot: victimSlot, gen: newGen, firstOwner: true }
    }
}

// ── §10.4 publishFaultState ──────────────────────────────────────

/**
 * Publish a fault completion into a slot.
 *
 * §10.4 contract:
 * - Acquires SLOT_LOCK[slot], validates pgno/gen/state=FETCHING, then updates
 * - If validation fails (slot was recycled), silently returns
 * - Copies data into SLOT_DATA if provided
 * - Bumps SLOT_WAITSEQ and notifies waiters
 *
 * Only Control Worker calls this.
 */
export function publishFaultState(
    slot: number,
    gen: bigint,
    pgno: number,
    newState: SlotState.ERROR | SlotState.READY | SlotState.RETRY,
    maybeData: Uint8Array | null,
    sab1: SharedArrayBuffer,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    acquireSlotLock(sab1, slot, pageSize)

    try {
        const meta = sab1SlotMetaView(sab1, slot, pageSize)
        // Validate slot identity
        if (Atomics.load(meta, SlotMetaI32.PGNO) !== pgno) return
        if (sab1LoadSlotGen(sab1, slot, pageSize) !== gen) return
        if (Atomics.load(meta, SlotMetaI32.STATE) !== SlotState.FETCHING) return

        // Copy data if provided
        if (maybeData != null) {
            const slotData = sab1SlotDataView(sab1, slot, pageSize)
            const len = Math.min(maybeData.length, pageSize)
            slotData.set(len < pageSize ? maybeData.subarray(0, len) : maybeData)
        }

        // Publish new state and wake waiters
        Atomics.store(meta, SlotMetaI32.STATE, newState)
        Atomics.add(meta, SlotMetaI32.WAITSEQ, 1)
        Atomics.notify(meta, SlotMetaI32.WAITSEQ, Infinity)
    } finally {
        releaseSlotLock(sab1, slot, pageSize)
    }
}

// ── §10.5 xWriteMainDb ──────────────────────────────────────────

/**
 * Best-effort cache write for MainDb page data.
 *
 * §10.5 contract:
 * - Uses PAGE_TABLE_LOCK → SLOT_LOCK[i] hierarchy
 * - May write READY directly with new bytes
 * - May evict EMPTY, ERROR, or READY slots
 * - Always succeeds (SQLITE_OK) even if no slot could be updated
 * - This is a cache write, not the source of truth
 */
export function xWriteMainDb(
    pgno: number,
    data: Uint8Array,
    sab1: SharedArrayBuffer,
    pageSize: number = DEFAULT_PAGE_SIZE,
    pageOffset: number = 0
): void {
    const ctrl = sab1CtrlView(sab1)
    acquirePageTableLock(sab1)

    try {
        // Try to update an existing cacheable slot for this pgno.
        // FETCHING/RETRY remain fault-owned until publishFaultState() wakes readers.
        for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
            const meta = sab1SlotMetaView(sab1, slot, pageSize)
            const st = Atomics.load(meta, SlotMetaI32.STATE)
            if (st !== SlotState.EMPTY && Atomics.load(meta, SlotMetaI32.PGNO) === pgno) {
                acquireSlotLock(sab1, slot, pageSize)
                try {
                    // Re-verify under slot lock
                    const st2 = Atomics.load(meta, SlotMetaI32.STATE)
                    const pg2 = Atomics.load(meta, SlotMetaI32.PGNO)
                    if ((st2 === SlotState.READY || st2 === SlotState.ERROR) && pg2 === pgno) {
                        const slotData = sab1SlotDataView(sab1, slot, pageSize)
                        const writeLength = Math.min(data.length, Math.max(0, pageSize - pageOffset))
                        const src = data.subarray(0, writeLength)
                        slotData.set(src, pageOffset)
                        const pageCount = Atomics.load(ctrl, SAB1_DB_PAGE_COUNT_I32)
                        if (pgno > pageCount) {
                            Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, pgno)
                        }
                        Atomics.store(meta, SlotMetaI32.STATE, SlotState.READY)
                    }
                } finally {
                    releaseSlotLock(sab1, slot, pageSize)
                }
                return
            }
        }

        // No existing slot — try to install into an evictable slot.
        // Prefer EMPTY, then ERROR, then READY so sequential priming does not
        // immediately evict freshly-installed cache entries.
        const victimStates = [SlotState.EMPTY, SlotState.ERROR, SlotState.READY] as const
        for (const victimState of victimStates) {
            for (let slot = 0; slot < NUM_SLOTS; slot += 1) {
                const meta = sab1SlotMetaView(sab1, slot, pageSize)
                if (Atomics.load(meta, SlotMetaI32.STATE) !== victimState) {
                    continue
                }

                acquireSlotLock(sab1, slot, pageSize)
                try {
                    if (Atomics.load(meta, SlotMetaI32.STATE) !== victimState) {
                        continue // state changed, try next slot
                    }

                    const newGen = sab1LoadSlotGen(sab1, slot, pageSize) + 1n
                    sab1StoreSlotGen(sab1, slot, newGen, pageSize)
                    Atomics.store(meta, SlotMetaI32.PGNO, pgno)

                    const slotData = sab1SlotDataView(sab1, slot, pageSize)
                    slotData.fill(0)
                    const writeLength = Math.min(data.length, Math.max(0, pageSize - pageOffset))
                    const src = data.subarray(0, writeLength)
                    slotData.set(src, pageOffset)
                    const pageCount = Atomics.load(ctrl, SAB1_DB_PAGE_COUNT_I32)
                    if (pgno > pageCount) {
                        Atomics.store(ctrl, SAB1_DB_PAGE_COUNT_I32, pgno)
                    }
                    Atomics.store(meta, SlotMetaI32.STATE, SlotState.READY)
                } finally {
                    releaseSlotLock(sab1, slot, pageSize)
                }
                return
            }
        }
        // No slot available — that's fine, it's just a cache
    } finally {
        releasePageTableLock(sab1)
    }
}

// ── Slot lock/data helpers ───────────────────────────────────────

/** Release SLOT_LOCK[slot] after pinOrInstallSlotForReadLinearized. */
export function unlockSlot(
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): void {
    releaseSlotLock(sab1, slot, pageSize)
}

/**
 * Copy page data from a slot into a destination buffer.
 * Caller must hold SLOT_LOCK[slot] to ensure data stability.
 * Returns the number of bytes copied (always pageSize).
 */
export function readSlotDataInto(
    dst: Uint8Array,
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE,
    pageOffset: number = 0
): number {
    const src = sab1SlotDataView(sab1, slot, pageSize)
    const start = Math.max(0, Math.min(pageOffset, src.length))
    const copied = Math.min(src.length - start, dst.length)
    if (copied > 0) {
        dst.set(src.subarray(start, start + copied))
    }
    return copied
}

/**
 * Get the current state of a slot.
 * For reliable results, caller should hold SLOT_LOCK.
 */
export function getSlotState(
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): SlotState {
    return Atomics.load(sab1SlotMetaView(sab1, slot, pageSize), SlotMetaI32.STATE) as SlotState
}

/**
 * Get the current pgno mapped by a slot.
 * For reliable results, caller should hold SLOT_LOCK.
 */
export function getSlotPgno(
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): number {
    return Atomics.load(sab1SlotMetaView(sab1, slot, pageSize), SlotMetaI32.PGNO)
}

/**
 * Get the current generation of a slot.
 * For reliable results, caller should hold SLOT_LOCK + PAGE_TABLE_LOCK.
 */
export function getSlotGen(
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): bigint {
    return sab1LoadSlotGen(sab1, slot, pageSize)
}

/**
 * Get the current SLOT_WAITSEQ value for a slot.
 * Used by xReadMainDb to implement Atomics.wait fallback.
 */
export function getSlotWaitSeq(
    sab1: SharedArrayBuffer,
    slot: number,
    pageSize: number = DEFAULT_PAGE_SIZE
): number {
    return Atomics.load(sab1SlotMetaView(sab1, slot, pageSize), SlotMetaI32.WAITSEQ)
}
